import {useState, useEffect, useCallback} from 'react'

/**
 * Custom hook for countdown with automatic refetch functionality
 * @param {Function} refetchFunction - Function to call when countdown reaches 0
 * @param {Object} options - Configuration options
 * @param {number} options.initialTime - Initial countdown time in seconds (default: 60)
 * @param {boolean} options.shouldRun - Whether countdown should be running (default: true)
 * @param {any} options.resetTrigger - Value that triggers countdown reset when changed
 * @returns {Object} - { countdown, handleRefetch, resetCountdown }
 */
const useCountdownRefetch = (refetchFunction, options = {}) => {
	const {initialTime = 60, shouldRun = true, resetTrigger} = options

	const [countdown, setCountdown] = useState(initialTime)

	// Memoize refetch wrapper to prevent unnecessary re-renders
	const handleRefetch = useCallback(() => {
		if (refetchFunction) {
			refetchFunction()
		}
		setCountdown(initialTime)
	}, [refetchFunction, initialTime])

	// Reset countdown function
	const resetCountdown = useCallback(() => {
		setCountdown(initialTime)
	}, [initialTime])

	// Reset countdown when resetTrigger changes
	useEffect(() => {
		if (resetTrigger) {
			resetCountdown()
		}
	}, [resetTrigger, resetCountdown])

	// Countdown timer - only runs when conditions are met
	useEffect(() => {
		if (!shouldRun) return

		const interval = setInterval(() => {
			setCountdown((prev) => {
				if (prev <= 1) {
					handleRefetch()
					return initialTime
				}
				return prev - 1
			})
		}, 1000)

		return () => clearInterval(interval)
	}, [shouldRun, handleRefetch, initialTime])

	return {
		countdown,
		handleRefetch,
		resetCountdown,
	}
}

export default useCountdownRefetch
